import { parseISO } from 'date-fns';
import { toTagOptions } from '../../../../../utils/transformers';
import { OVERTIME_FIELD_LABELS, OVERTIME_GRID_COLUMN_LABELS, OVERTIME_SECTION_KEYS } from './constants';
import { OvertimeWorkedRow } from './types';

export const OVERTIME_WORKED_GRID_KEY = 'overtime_worked';

export const OVERTIME_CLIENT_MATTER_ROW_FIELDS = {
  picker: 'client_matter',
  clientName: 'client_name',
  matterName: 'matter_name',
  clientUno: 'client_uno',
  matterUno: 'matter_uno',
  clientCode: 'client_code',
  matterCode: 'matter_code',
  matterNumber: 'matter_number',
  selectedClientLabel: 'selected_client_label',
  selectedMatterLabel: 'selected_matter_label',
} as const;

export const getOvertimeClientMatterFieldExtras = (
  office?: string
): Record<string, unknown> => {
  return {
    clientNameField: OVERTIME_CLIENT_MATTER_ROW_FIELDS.clientName,
    matterNameField: OVERTIME_CLIENT_MATTER_ROW_FIELDS.matterName,
    clientUnoField: OVERTIME_CLIENT_MATTER_ROW_FIELDS.clientUno,
    matterUnoField: OVERTIME_CLIENT_MATTER_ROW_FIELDS.matterUno,
    clientCodeField: OVERTIME_CLIENT_MATTER_ROW_FIELDS.clientCode,
    matterCodeField: OVERTIME_CLIENT_MATTER_ROW_FIELDS.matterNumber,
    selectedClientLabelField:
      OVERTIME_CLIENT_MATTER_ROW_FIELDS.selectedClientLabel,
    selectedMatterLabelField:
      OVERTIME_CLIENT_MATTER_ROW_FIELDS.selectedMatterLabel,
    office,
    clientLabel: 'Client',
    matterLabel: 'Matter',
    requireMatter: true,
    showMatter: true,
  };
};

export const getMatterNumberFromSelectionLabel = (label: unknown): string => {
  if (typeof label !== 'string' || label.trim() === '') {
    return '';
  }

  const match = /\(([^)]+)\)\s*$/.exec(label.trim());
  return match?.[1] ?? '';
};

const MINUTES_IN_DAY = 24 * 60;
const TIME_PATTERN = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isValidDate = (value: Date): boolean => {
  return !Number.isNaN(value.getTime());
};

export const parseOvertimeDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsedIso = parseISO(value.trim());
  if (isValidDate(parsedIso)) {
    return parsedIso;
  }

  const parsedFallback = new Date(value);
  return isValidDate(parsedFallback) ? parsedFallback : null;
};

export const getDayNameFromDate = (value: unknown): string => {
  const date = parseOvertimeDate(value);
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(date);
};

export const parseTimeToMinutes = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const match = TIME_PATTERN.exec(String(value).trim());
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours > 23 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

export const calculateOvertimeHours = (
  fromTime: unknown,
  toTime: unknown
): string => {
  const fromMinutes = parseTimeToMinutes(fromTime);
  const toMinutes = parseTimeToMinutes(toTime);
  if (fromMinutes === null || toMinutes === null) {
    return '';
  }

  let diffMinutes = toMinutes - fromMinutes;
  if (diffMinutes < 0) {
    diffMinutes += MINUTES_IN_DAY;
  }

  const hours = Math.round((diffMinutes / 60) * 100) / 100;
  return String(hours);
};

export const enrichOvertimeWorkedRow = (
  row: OvertimeWorkedRow
): OvertimeWorkedRow => {
  const matterNumberFromLabel = getMatterNumberFromSelectionLabel(
    row.selected_matter_label
  );

  return {
    ...row,
    day: getDayNameFromDate(row.date),
    hours: calculateOvertimeHours(row.from_time, row.to_time),
    ...(matterNumberFromLabel ? { matter_number: matterNumberFromLabel } : {}),
  };
};

export const applyOvertimeWorkedRowAutomation = (row: unknown): void => {
  if (!isRecord(row)) {
    return;
  }

  row.day = getDayNameFromDate(row.date);
  row.hours = calculateOvertimeHours(row.from_time, row.to_time);

  const matterNumberFromLabel = getMatterNumberFromSelectionLabel(
    row[OVERTIME_CLIENT_MATTER_ROW_FIELDS.selectedMatterLabel]
  );
  if (matterNumberFromLabel) {
    row[OVERTIME_CLIENT_MATTER_ROW_FIELDS.matterNumber] =
      matterNumberFromLabel;
  }
};

const toGridCellString = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
};

export const toOvertimeWorkedSubmitRow = (
  row: OvertimeWorkedRow
): Record<string, string> => {
  const enriched = enrichOvertimeWorkedRow(row);

  return {
    date: toGridCellString(enriched.date),
    day: toGridCellString(enriched.day),
    from_time: toGridCellString(enriched.from_time),
    to_time: toGridCellString(enriched.to_time),
    hours: toGridCellString(enriched.hours),
    matter_number: toGridCellString(enriched.matter_number),
    reason: toGridCellString(enriched.reason),
  };
};

const isOvertimeWorkedRow = (value: unknown): value is OvertimeWorkedRow => {
  return isRecord(value);
};

export const extractOvertimeWorkedRows = (
  sectionGridData: unknown
): OvertimeWorkedRow[] => {
  if (Array.isArray(sectionGridData)) {
    return sectionGridData.filter(isOvertimeWorkedRow);
  }

  if (!isRecord(sectionGridData)) {
    return [];
  }

  if (Array.isArray(sectionGridData[OVERTIME_WORKED_GRID_KEY])) {
    return sectionGridData[OVERTIME_WORKED_GRID_KEY].filter(isOvertimeWorkedRow);
  }

  if (Array.isArray(sectionGridData.data)) {
    return sectionGridData.data.filter(isOvertimeWorkedRow);
  }

  return [];
};

export interface OvertimeRowValidationResult {
  success: boolean;
  errors?: Record<string, string>;
}

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === '';

export const validateOvertimeWorkedRow = (
  row: unknown
): OvertimeRowValidationResult => {
  if (!isRecord(row)) {
    return {
      success: false,
      errors: {
        date: `${OVERTIME_GRID_COLUMN_LABELS.date} is required`,
      },
    };
  }

  const errors: Record<string, string> = {};

  if (!parseOvertimeDate(row.date)) {
    errors.date = `${OVERTIME_GRID_COLUMN_LABELS.date} is required`;
  }

  if (parseTimeToMinutes(row.from_time) === null) {
    errors.from_time = `${OVERTIME_GRID_COLUMN_LABELS.fromTime} is required`;
  }

  if (parseTimeToMinutes(row.to_time) === null) {
    errors.to_time = `${OVERTIME_GRID_COLUMN_LABELS.toTime} is required`;
  }

  if (
    parseTimeToMinutes(row.from_time) !== null &&
    parseTimeToMinutes(row.to_time) !== null
  ) {
    const hours = calculateOvertimeHours(row.from_time, row.to_time);
    if (hours === '' || Number(hours) <= 0) {
      errors.to_time = `${OVERTIME_GRID_COLUMN_LABELS.toTime} must be after ${OVERTIME_GRID_COLUMN_LABELS.fromTime}`;
    }
  }

  if (isBlank(row.reason)) {
    errors.reason = `${OVERTIME_GRID_COLUMN_LABELS.reason} is required`;
  }

  if (Object.keys(errors).length > 0) {
    return { success: false, errors };
  }

  return { success: true };
};

export const validateOvertimeWorkedGrid = (
  value: unknown
): string | undefined => {
  const rows = extractOvertimeWorkedRows(value);
  if (rows.length === 0) {
    return `Please add at least one ${OVERTIME_FIELD_LABELS.overtimeWorked.toLowerCase()} entry`;
  }

  for (const [index, row] of rows.entries()) {
    const result = validateOvertimeWorkedRow(row);
    if (!result.success) {
      const firstError = Object.values(result.errors ?? {})[0];
      return `Row ${index + 1}: ${firstError}`;
    }
  }

  return undefined;
};

const hasMatterCellValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return value !== null && value !== undefined && value !== '';
};

const overtimeRowHasMatter = (row: OvertimeWorkedRow): boolean => {
  if (
    hasMatterCellValue(row.matter_number) ||
    hasMatterCellValue(row.matter_uno) ||
    hasMatterCellValue(row.matter_name) ||
    hasMatterCellValue(row.selected_matter_label)
  ) {
    return true;
  }

  const picker = (row as unknown as Record<string, unknown>).client_matter;
  if (!isRecord(picker)) {
    return false;
  }

  return (
    hasMatterCellValue(picker.matterName) ||
    hasMatterCellValue(picker.matterUno) ||
    hasMatterCellValue(picker.matter_number)
  );
};

const resolveOvertimeWorkedRows = (formOrGrid: unknown): OvertimeWorkedRow[] => {
  const directRows = extractOvertimeWorkedRows(formOrGrid);
  if (directRows.length > 0) {
    return directRows;
  }

  if (!isRecord(formOrGrid)) {
    return [];
  }

  return extractOvertimeWorkedRows(formOrGrid[OVERTIME_WORKED_GRID_KEY]);
};

export const hasOvertimeMatterInGrid = (formOrGrid: unknown): boolean => {
  return resolveOvertimeWorkedRows(formOrGrid).some(overtimeRowHasMatter);
};

export const getOvertimeSectionFlow = (formOrGrid: unknown): string[] => {
  const flow = [
    OVERTIME_SECTION_KEYS.requestDetails,
    OVERTIME_SECTION_KEYS.lineManager,
    OVERTIME_SECTION_KEYS.financeConfirmation,
  ];

  if (hasOvertimeMatterInGrid(formOrGrid)) {
    return flow;
  }

  return flow.filter(
    (section) => section !== OVERTIME_SECTION_KEYS.financeConfirmation
  );
};

export const toOvertimeFormGridData = (
  rows: OvertimeWorkedRow[]
): { data: OvertimeWorkedRow[] } => {
  return { data: rows };
};

export const buildOvertimeWorkedSubmitGrid = (
  formGridData: unknown
): Record<string, Record<string, string>[]> => {
  const rows = extractOvertimeWorkedRows(formGridData).map(
    toOvertimeWorkedSubmitRow
  );
  if (rows.length === 0) {
    return {};
  }

  return { [OVERTIME_WORKED_GRID_KEY]: rows };
};

export const normalizeLineManagerForForm = (
  value: unknown
): ReturnType<typeof toTagOptions> => {
  return toTagOptions(value);
};

export const getLineManagerMailForSubmit = (
  value: unknown
): string | undefined => {
  const mail = toTagOptions(value)[0]?.value?.trim();
  return mail ? mail : undefined;
};

const OVERTIME_ASSIGNED_SECTIONS = Object.values(OVERTIME_SECTION_KEYS);

export const isOvertimeApprovalSectionKey = (sectionKey: string): boolean =>
  (OVERTIME_ASSIGNED_SECTIONS as string[]).includes(sectionKey);

/**
 * TEMPORARY: Overtime Approval backend permissions are not ready yet.
 * Treat the user as assigned to every overtime section so Request Details
 * and later stages stay editable without backend assignment flags.
 */
export const withTemporaryOvertimeAssignedSections = (
  assignedSections: string[] | undefined
): string[] => {
  const existing = Array.isArray(assignedSections) ? assignedSections : [];
  return Array.from(new Set([...existing, ...OVERTIME_ASSIGNED_SECTIONS]));
};
